import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'

const t = buildTranslate('recovery_ai.sandbox')

type Props = {
  /**
   * `loading` → a `role="status"` region with an sr-only label so a screen
   * reader announces the wait (the bare `<Spinner>` carries no accessible name;
   * WCAG 4.1.3 Status Messages). `error` → a recoverable `role="alert"` message
   * for a failed config/rewards GET — distinct from the in-tab "configure first"
   * gate and from the engine's in-chat `connectionError`.
   */
  variant: 'loading' | 'error'
  /**
   * AI-185: when provided, the error variant renders a Retry that re-runs the
   * failed GET in place so a transient failure doesn't dead-end the Testing gate.
   */
  onRetry?: () => void
}

// Shared loading / error states for the two simulator shells (standalone,
// wizard). Centralizing them keeps the a11y wrapper and the error copy
// consistent and fixes the wizard's previously unrecoverable spinner: a
// config GET that errors now renders this message instead of spinning forever.
const SimulatorStatus = ({ variant, onRetry }: Props) => {
  const [css] = useStyletron()

  if (variant === 'error') {
    return (
      <div
        role="alert"
        className={css({ textAlign: 'center', padding: '48px 0' })}
      >
        <Text.BodyText3 color="cayenne50">{t('load_error')}</Text.BodyText3>
        {onRetry && (
          <div className={css({ marginTop: '16px' })}>
            <Button kind="secondary" onClick={onRetry}>
              {t('load_error_retry')}
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div role="status" className="text-center margin-top-huge">
      <Spinner isLoading size="4x" />
      <span className="sr-only">{t('loading')}</span>
    </div>
  )
}

export default SimulatorStatus
